import { useQueryClient } from '@tanstack/react-query';
import { Study } from 'interfaces/study';
import { uniq } from 'lodash';
import { useMemo, useState } from 'react';
import { invalidateSlideVisibility } from 'utils/queryHooks/invalidateSlideVisibility';

import { normalizeDismissedCaseOrganizationSlideIds } from './caseOrganizationSlideDismissal';
import { saveDismissedCaseOrganizationSlideIds } from './caseOrganizationSlideDismissalClient';

export const useCaseOrganizationSlideDismissal = (study?: Study) => {
  const queryClient = useQueryClient();
  const [addedSlideIds, setAddedSlideIds] = useState<string[]>([]);
  const [removedSlideIds, setRemovedSlideIds] = useState<string[]>([]);
  const [savingSlideId, setSavingSlideId] = useState<string>();
  const [error, setError] = useState<string>();
  const storedSlideIds = study?.settings?.dismissedCaseOrganizationSlideIds;
  const [storedScope, setStoredScope] = useState({ studyId: study?.id, slideIds: storedSlideIds });
  if (storedScope.studyId !== study?.id || storedScope.slideIds !== storedSlideIds) {
    setStoredScope({ studyId: study?.id, slideIds: storedSlideIds });
    setAddedSlideIds([]);
    setRemovedSlideIds([]);
  }
  const dismissedSlideIds = useMemo(
    () =>
      normalizeDismissedCaseOrganizationSlideIds(
        uniq([...(storedSlideIds || []), ...addedSlideIds]).filter((slideId) => !removedSlideIds.includes(slideId))
      ),
    [addedSlideIds, removedSlideIds, storedSlideIds]
  );

  const saveSlideIds = async (slideId: string, isHidden: boolean, failureMessage: string) => {
    if (!study?.id || savingSlideId) {
      return false;
    }
    setSavingSlideId(slideId);
    setError(undefined);
    try {
      await saveDismissedCaseOrganizationSlideIds({ study, slideId, isHidden });
      await invalidateSlideVisibility(queryClient, study.id);
      return true;
    } catch {
      setError(failureMessage);
      return false;
    } finally {
      setSavingSlideId(undefined);
    }
  };

  return {
    dismissedSlideIds,
    savingSlideId,
    error,
    hideSlide: async (slideId: string) => {
      setAddedSlideIds((current) => uniq([...current, slideId]));
      setRemovedSlideIds((current) => current.filter((id) => id !== slideId));
      const saved = await saveSlideIds(slideId, true, 'This slide could not be hidden. It is still in the notice.');
      if (!saved) {
        setAddedSlideIds((current) => current.filter((id) => id !== slideId));
      }
    },
    restoreSlide: async (slideId: string) => {
      setRemovedSlideIds((current) => uniq([...current, slideId]));
      setAddedSlideIds((current) => current.filter((id) => id !== slideId));
      const saved = await saveSlideIds(slideId, false, 'This slide could not be restored. It is still hidden.');
      if (!saved) {
        setRemovedSlideIds((current) => current.filter((id) => id !== slideId));
      }
    },
  };
};
